import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '../app/injection-tokens';
import { HttpClient } from '@angular/common/http';
import { forkJoin, map, Observable, switchMap } from 'rxjs';
import { IRestResponse, OpportunityQuote, QuoteDownloadLink } from '@pure1/data';

//This endpoint is for downloading ONE file with READ_PARTNER_QUOTE Permission
const DOWNLOAD_ENDPOINT = '/rest/v5/quotes/download-url';

//This endpoint is for downloading ONE file with READ_OPPORTUNITY Permission
const SERVICE_ACTIVATION_DOWNLOAD_ENDPOINT = '/rest/v5/quotes/service-activation-download-url';

//This endpoint is for downloading MULTIPLE file with READ_PARTNER_QUOTE Permission
const QUOTE_MANAGEMENT_ENDPOINT = '/rest/v1/quote-management';
const BULK_DOWNLOAD_ENDPOINT = QUOTE_MANAGEMENT_ENDPOINT + '/bulk-download';

export type DownloadUrlParamIdType = 'PO' | 'QUOTE' | 'SERVICE_ACTIVATION';

interface QuoteBlob {
    id: string;
    blob: Blob;
}

@Injectable({ providedIn: 'root' })
export class DownloadUrlService {
    constructor(
        @Inject(WINDOW) private window: Window,
        private http: HttpClient,
    ) {}

    getOneFileDownloadUrlWith302Response(
        salesFlowId: string,
        id: string,
        type: DownloadUrlParamIdType = 'QUOTE',
    ): void {
        const urlToUse = this.generateDownloadPdfUrlWith302Response(salesFlowId, id, type);
        this.window.open(urlToUse, '_blank');
    }

    getMultipleFileDownloadUrl(quotes: OpportunityQuote[]): Observable<QuoteDownloadLink[]> {
        return this.http
            .get<IRestResponse<QuoteDownloadLink>>(BULK_DOWNLOAD_ENDPOINT, {
                observe: 'response',
                params: {
                    ids: quotes.map(quote => quote.id).join(','),
                },
            })
            .pipe(map(response => response.body?.items || []));
    }

    getDownloadLinksAsBlobs(quotes: OpportunityQuote[]): Observable<QuoteBlob[]> {
        return this.getMultipleFileDownloadUrl(quotes).pipe(
            switchMap(response =>
                forkJoin(
                    response
                        .filter(link => link?.download_link)
                        .map(link => {
                            return this.http.get(link.download_link, { responseType: 'blob' }).pipe(
                                map(blob => {
                                    return {
                                        id: link.quote_id,
                                        blob,
                                    } as QuoteBlob;
                                }),
                            );
                        }),
                ),
            ),
        );
    }

    private generateDownloadPdfUrlWith302Response(
        salesFlowId: string,
        id: string,
        type: DownloadUrlParamIdType = 'QUOTE',
    ): string {
        if (!salesFlowId || !id) {
            return '';
        }

        const new_params = new URLSearchParams({
            salesFlowId: salesFlowId,
            id: id,
            type: type,
        });

        return `${this.window.location.origin}${this.getDownloadEndpoint(type)}?${new_params}`;
    }

    private getDownloadEndpoint(type: DownloadUrlParamIdType): string {
        switch (type) {
            case 'SERVICE_ACTIVATION':
                return SERVICE_ACTIVATION_DOWNLOAD_ENDPOINT;
            default:
                return DOWNLOAD_ENDPOINT;
        }
    }
}
